import { useState, useEffect } from 'react';

/**
 * Custom hook to detect user's motion preference
 * Respects the prefers-reduced-motion media query for accessibility
 * @returns {boolean} - true if user prefers reduced motion
 */
export const useReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if the browser supports the matchMedia API
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Set initial value
    setPrefersReducedMotion(mediaQuery.matches);

    // Create event listener for changes
    const handleChange = (event) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return prefersReducedMotion;
};

/**
 * Get animation variants based on motion preference
 * @param {boolean} prefersReducedMotion - User's motion preference
 * @returns {object} - Framer Motion animation variants
 */
export const getAnimationVariants = (prefersReducedMotion) => {
  if (prefersReducedMotion) {
    return {
      initial: { opacity: 1 },
      animate: { opacity: 1 },
      exit: { opacity: 1 },
      transition: { duration: 0 }
    };
  }

  return {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0 },
    transition: { duration: 0.5 }
  };
};

/**
 * Get fade-in animation configuration
 * @param {boolean} prefersReducedMotion - User's motion preference
 * @param {number} delay - Animation delay in seconds
 * @returns {object} - Framer Motion props
 */
export const getFadeInAnimation = (prefersReducedMotion, delay = 0) => {
  if (prefersReducedMotion) {
    return {
      initial: { opacity: 1 },
      whileInView: { opacity: 1 },
      viewport: { once: true },
      transition: { duration: 0 }
    };
  }

  return {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5, delay }
  };
};

/**
 * Get scale animation configuration
 * @param {boolean} prefersReducedMotion - User's motion preference
 * @param {number} delay - Animation delay in seconds
 * @returns {object} - Framer Motion props
 */
export const getScaleAnimation = (prefersReducedMotion, delay = 0) => {
  if (prefersReducedMotion) {
    return {
      initial: { opacity: 1, scale: 1 },
      whileInView: { opacity: 1, scale: 1 },
      viewport: { once: true },
      transition: { duration: 0 }
    };
  }

  return {
    initial: { opacity: 0, scale: 0.9 },
    whileInView: { opacity: 1, scale: 1 },
    viewport: { once: true },
    transition: { duration: 0.4, delay }
  };
};

export default useReducedMotion;
